import React, { useState, useEffect } from 'react';
import { Trophy, Target, TrendingUp, Calendar, Filter, Users, ChevronDown, X, BarChart3 } from 'lucide-react';
import AllTimeStandingsBanner from './AllTimeStandingsBanner';

const AllTimeStandingsComponent = ({ h2hData: propH2hData, finalData: propFinalData, loading: propLoading, error: propError }) => {
  const [standingsData, setStandingsData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [imageErrors, setImageErrors] = useState(new Set());
  const [isMobile, setIsMobile] = useState(false);
  const [filters, setFilters] = useState({
    gameTypes: ['Regular Season'],
    yearRange: [2012, 2023],
    sortBy: 'WinPct'
  });
  const [rawData, setRawData] = useState({ finalData: [], h2hData: [] });
  const [availableYears, setAvailableYears] = useState({ min: 2012, max: 2023 });

  const gameTypeOptions = ['Regular Season', 'Playoffs', 'Consolation'];
  const sortOptions = [
    { value: 'WinPct', label: 'Win Percentage' },
    { value: 'Wins', label: 'Total Wins' },
    { value: 'Losses', label: 'Total Losses' },
    { value: 'GP', label: 'Games Played' },
    { value: 'PF', label: 'Total Points For' },
    { value: 'PA', label: 'Total Points Against' },
    { value: 'AvgPF', label: 'Average Points For' },
    { value: 'AvgPA', label: 'Average Points Against' }
  ];

  // Check for mobile screen size
  useEffect(() => {
    const checkMobile = () => {
      setIsMobile(window.innerWidth < 768);
    };
    
    checkMobile();
    window.addEventListener('resize', checkMobile);
    return () => window.removeEventListener('resize', checkMobile);
  }, []);

  const handleImageError = (managerName) => {
    setImageErrors(prev => new Set(prev).add(managerName));
  };

  useEffect(() => {
    // If data is passed as props (from main app), use that
    if (propH2hData && propH2hData.length > 0) {
      console.log('Using data passed as props');
      setRawData({ 
        h2hData: propH2hData, 
        finalData: propFinalData || [] 
      });
      setLoading(propLoading || false);
      setError(propError || null);
      
      // Set dynamic year range from prop data
      const years = [...new Set(propH2hData.map(row => row.Year))]
        .filter(year => year && !isNaN(year) && year > 1900)
        .sort((a, b) => a - b);
      
      if (years.length > 0) {
        const minYear = Math.min(...years);
        const maxYear = Math.max(...years);
        setAvailableYears({ min: minYear, max: maxYear });
        setFilters(prev => ({
          ...prev,
          yearRange: [minYear, maxYear]
        }));
      }
    } else {
      // Otherwise, load data independently
      console.log('Loading data independently');
      loadData();
    }
  }, [propH2hData, propFinalData, propLoading, propError]);

  useEffect(() => {
    if (rawData.h2hData.length > 0) {
      calculateStandings();
    }
  }, [filters, rawData]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // Use the same approach as your existing app - fetch from public folder
      const response = await fetch('/BoBData.xlsx');
      if (!response.ok) {
        throw new Error(`Could not fetch Excel file: ${response.status} ${response.statusText}`);
      }
      const arrayBuffer = await response.arrayBuffer();
      
      // Load XLSX library from CDN if not already loaded
      if (!window.XLSX) {
        const script = document.createElement('script');
        script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
        script.onload = () => {
          parseExcelData(arrayBuffer);
        };
        script.onerror = () => {
          setError('Failed to load XLSX library');
          setLoading(false);
        };
        document.head.appendChild(script);
      } else {
        parseExcelData(arrayBuffer);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      setError(`Error loading data: ${error.message}`);
      setLoading(false);
    }
  };

  const parseExcelData = (arrayBuffer) => {
    try {
      // Parse the workbook using XLSX library
      const workbook = window.XLSX.read(arrayBuffer, { type: 'array' });
      
      console.log('Available sheets:', workbook.SheetNames);
      
      // Read H2H sheet - this is the primary data source per R code
      if (!workbook.Sheets['H2H']) {
        throw new Error('H2H sheet not found in Excel file');
      }
      
      const h2hSheet = workbook.Sheets['H2H'];
      const h2hData = window.XLSX.utils.sheet_to_json(h2hSheet);
      
      console.log('H2H Data loaded:', h2hData.length, 'records');
      console.log('Sample H2H record:', h2hData[0]);
      console.log('H2H columns:', Object.keys(h2hData[0] || {}));
      
      // Read Final Data sheet as backup/reference
      let finalData = [];
      if (workbook.Sheets['Final Data']) {
        const finalDataSheet = workbook.Sheets['Final Data'];
        finalData = window.XLSX.utils.sheet_to_json(finalDataSheet);
        console.log('Final Data loaded:', finalData.length, 'records');
      }

      // Validate required fields exist in H2H data
      if (h2hData.length > 0) {
        const requiredFields = ['Manager1', 'Type', 'Year', 'PF', 'PA', 'IsWin', 'IsLoss'];
        const sampleRecord = h2hData[0];
        const missingFields = requiredFields.filter(field => !(field in sampleRecord));
        
        if (missingFields.length > 0) {
          console.warn('Missing fields in H2H data:', missingFields);
          console.log('Available fields:', Object.keys(sampleRecord));
        }
      }

      setRawData({ finalData, h2hData });
      
      // Set dynamic year range based on available H2H data (primary source)
      if (h2hData.length > 0) {
        const years = [...new Set(h2hData.map(row => row.Year))]
          .filter(year => year && !isNaN(year) && year > 1900)
          .sort((a, b) => a - b);
        
        if (years.length > 0) {
          const minYear = Math.min(...years);
          const maxYear = Math.max(...years);
          
          console.log('Year range detected:', minYear, '-', maxYear);
          
          setAvailableYears({ min: minYear, max: maxYear });
          setFilters(prev => ({
            ...prev,
            yearRange: [minYear, maxYear]
          }));
        }
      }
      
      setLoading(false);
      
    } catch (error) {
      console.error('Error parsing Excel data:', error);
      setError(`Error parsing Excel data: ${error.message}`);
      setLoading(false);
    }
  };

  const calculateStandings = () => {
    const { h2hData } = rawData;
    
    if (!h2hData || h2hData.length === 0) return;

    // Filter data based on selected criteria - matching R code: filter(h2h, Type %in% input$selected_gametype2) %>% filter(Year %in% years2())
    const filteredH2H = h2hData.filter(game => 
      filters.gameTypes.includes(game.Type) &&
      game.Year >= filters.yearRange[0] && 
      game.Year <= filters.yearRange[1] &&
      game.Manager1 && 
      (game.PF !== undefined && game.PF !== null) &&
      (game.PA !== undefined && game.PA !== null) &&
      (game.IsWin !== undefined && game.IsWin !== null) &&
      (game.IsLoss !== undefined && game.IsLoss !== null)
    );

    console.log('Filtered H2H data:', filteredH2H.length, 'games');
    console.log('Sample game:', filteredH2H[0]);

    // Calculate standings for each manager - matching R code: group_by(Manager1) %>% summarise(...)
    const managerStats = {};
    
    filteredH2H.forEach(game => {
      const manager = game.Manager1;
      
      if (!managerStats[manager]) {
        managerStats[manager] = {
          Manager: manager,
          Wins: 0,
          Losses: 0,
          PF: 0,
          PA: 0,
          GP: 0
        };
      }
      
      const stats = managerStats[manager];
      const pf = parseFloat(game.PF) || 0;
      const pa = parseFloat(game.PA) || 0;
      const isWin = parseInt(game.IsWin) || 0;
      const isLoss = parseInt(game.IsLoss) || 0;
      
      // Match R calculation exactly: Wins = sum(IsWin), Losses = sum(IsLoss)
      stats.Wins += isWin;
      stats.Losses += isLoss;
      stats.PF += pf;
      stats.PA += pa;
      stats.GP = stats.Wins + stats.Losses; // GP = Wins + Losses (matching R)
    });

    // Calculate derived stats and format - matching R code rounding
    const standings = Object.values(managerStats)
      .filter(stats => stats.GP > 0) // Only include managers with games played
      .map(stats => ({
        Manager: stats.Manager,
        Wins: stats.Wins,
        Losses: stats.Losses,
        WinPct: Math.round((stats.Wins / stats.GP) * 1000) / 1000, // round(Wins / GP, 3)
        GP: stats.GP,
        PF: Math.round(stats.PF * 100) / 100, // round(sum(PF), 2)
        PA: Math.round(stats.PA * 100) / 100, // sum(PA) - R doesn't round PA but we will for consistency
        AvgPF: Math.round((stats.PF / stats.GP) * 100) / 100, // round(PF / GP, 2)
        AvgPA: Math.round((stats.PA / stats.GP) * 100) / 100  // round(PA / GP, 2)
      }));

    console.log('Calculated standings:', standings);

    // Sort standings - matching R code: arrange(desc(WinPct))
    standings.sort((a, b) => {
      if (filters.sortBy === 'WinPct') {
        // Primary sort by WinPct, secondary by PF (common fantasy football tiebreaker)
        if (Math.abs(a.WinPct - b.WinPct) < 0.001) {
          return b.PF - a.PF;
        }
        return b.WinPct - a.WinPct;
      } else if (filters.sortBy === 'Wins') {
        return b.Wins - a.Wins;
      } else if (filters.sortBy === 'Losses') {
        return b.Losses - a.Losses;
      } else if (filters.sortBy === 'GP') {
        return b.GP - a.GP;
      } else if (filters.sortBy === 'PF') {
        return b.PF - a.PF;
      } else if (filters.sortBy === 'PA') {
        return b.PA - a.PA;
      } else if (filters.sortBy === 'AvgPF') {
        return b.AvgPF - a.AvgPF;
      } else if (filters.sortBy === 'AvgPA') {
        return b.AvgPA - a.AvgPA;
      }
      return 0;
    });

    setStandingsData(standings);
    setFilteredData(standings);
  };

  const handleFilterChange = (filterType, value) => {
    setFilters(prev => ({
      ...prev,
      [filterType]: value
    }));
  };

  const getPositionColor = (position) => {
    if (position === 1) return 'text-yellow-600 bg-yellow-50';
    if (position === 2) return 'text-gray-600 bg-gray-50';
    if (position === 3) return 'text-amber-600 bg-amber-50';
    if (position <= 6) return 'text-green-600 bg-green-50';
    return 'text-gray-800 bg-white';
  };

  const formatValue = (value, type) => {
    if (type === 'percentage') {
      return (value * 100).toFixed(1) + '%';
    }
    if (type === 'decimal') {
      return value.toFixed(2);
    }
    if (type === 'number') {
      // Add commas for thousands
      return value.toLocaleString('en-US');
    }
    if (type === 'decimal_with_commas') {
      // Add commas for thousands and show 2 decimal places
      return value.toLocaleString('en-US', { 
        minimumFractionDigits: 2, 
        maximumFractionDigits: 2 
      });
    }
    return value.toString();
  };

  const LoadingSpinner = ({ message = "Loading standings data..." }) => (
    <div className="flex items-center justify-center min-h-96">
      <div className="text-center">
        <div className="animate-spin rounded-full h-16 w-16 border-b-2 border-purple-600 mx-auto mb-4"></div>
        <p className="text-gray-600">{message}</p>
      </div>
    </div>
  );

  const ErrorMessage = ({ error }) => (
    <div className="bg-red-50 border border-red-200 rounded-xl p-6">
      <h3 className="text-red-800 font-semibold mb-2">Error loading data</h3>
      <p className="text-red-600">{error}</p>
      <div className="mt-4 text-sm text-red-700">
        <p>Please check that:</p>
        <ul className="list-disc list-inside mt-2 space-y-1">
          <li>BoBData.xlsx file is accessible</li>
          <li>H2H sheet exists with required columns (Manager1, Type, Year, PF, PA, IsWin, IsLoss)</li>
          <li>Data contains valid records</li>
        </ul>
      </div>
    </div>
  );

  // Mobile Card Component for Individual Manager
  const MobileManagerCard = ({ manager, index }) => (
    <div className="bg-white rounded-xl shadow-lg border border-gray-100 overflow-hidden">
      {/* Header with Rank and Manager */}
      <div className="bg-gradient-to-r from-purple-50 to-blue-50 p-4 border-b border-gray-100">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            {/* Rank Badge */}
            <span className={`inline-flex items-center justify-center w-10 h-10 rounded-full text-lg font-bold ${
              index === 0 ? 'bg-yellow-400 text-yellow-900' :
              index === 1 ? 'bg-gray-400 text-gray-900' :
              index === 2 ? 'bg-amber-400 text-amber-900' :
              'bg-gray-200 text-gray-700'
            }`}>
              {index + 1}
            </span>
            
            {/* Manager Avatar and Name */}
            <div className="flex items-center space-x-3">
              <div className="w-12 h-12 rounded-xl overflow-hidden border-2 border-white shadow-lg bg-gradient-to-br from-purple-100 to-blue-100">
                {!imageErrors.has(manager.Manager) ? (
                  <img 
                    src={`/${manager.Manager.toLowerCase()}main.png`} 
                    alt={`${manager.Manager} headshot`}
                    className="w-full h-full object-cover"
                    onError={() => handleImageError(manager.Manager)}
                  />
                ) : (
                  <div className="w-full h-full flex items-center justify-center text-lg font-bold text-purple-600">
                    {manager.Manager.charAt(0)}
                  </div>
                )}
              </div>
              <div>
                <h3 className="text-lg font-bold text-gray-900">{manager.Manager}</h3>
                <p className="text-sm text-gray-600">{manager.Wins}-{manager.Losses} Record</p>
              </div>
            </div>
          </div>
          
          {/* Win Percentage Badge */}
          <div className="text-right">
            <div className={`text-2xl font-bold ${
              manager.WinPct >= 0.6 ? 'text-green-600' : 
              manager.WinPct >= 0.4 ? 'text-yellow-600' : 
              'text-red-600'
            }`}>
              {formatValue(manager.WinPct, 'percentage')}
            </div>
            <div className="text-sm text-gray-500">Win Rate</div>
          </div>
        </div>
      </div>
      
      {/* Stats Grid */}
      <div className="p-4">
        <div className="grid grid-cols-2 gap-4">
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900">{formatValue(manager.GP, 'number')}</div>
            <div className="text-xs text-gray-600">Games Played</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900">{formatValue(manager.AvgPF, 'decimal')}</div>
            <div className="text-xs text-gray-600">Avg Points For</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900">{formatValue(manager.PF, 'decimal_with_commas')}</div>
            <div className="text-xs text-gray-600">Total PF</div>
          </div>
          <div className="bg-gray-50 rounded-lg p-3 text-center">
            <div className="text-xl font-bold text-gray-900">{formatValue(manager.AvgPA, 'decimal')}</div>
            <div className="text-xs text-gray-600">Avg Points Against</div>
          </div>
        </div>
      </div>
    </div>
  );

  if (loading) {
    return <LoadingSpinner />;
  }

  if (error) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Banner Header */}
        <AllTimeStandingsBanner />

        {/* Filters Card */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Game Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Filter className="inline-block w-4 h-4 mr-2" />
                Game Types
              </label>
              <div className="space-y-2">
                {gameTypeOptions.map(type => (
                  <label key={type} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.gameTypes.includes(type)}
                      onChange={(e) => {
                        const newTypes = e.target.checked
                          ? [...filters.gameTypes, type]
                          : filters.gameTypes.filter(t => t !== type);
                        handleFilterChange('gameTypes', newTypes);
                      }}
                      className="mr-3 h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Year Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Calendar className="inline-block w-4 h-4 mr-2" />
                Year Range
              </label>
              <div className="space-y-4">
                {/* Custom Range Slider */}
                <div className="relative">
                  <div 
                    className="range-slider-container relative h-2 bg-gray-200 rounded-lg"
                  >
                    <div 
                      className="range-slider-track absolute h-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg"
                      style={{
                        left: `${((filters.yearRange[0] - availableYears.min) / (availableYears.max - availableYears.min)) * 100}%`,
                        width: `${((filters.yearRange[1] - filters.yearRange[0]) / (availableYears.max - availableYears.min)) * 100}%`
                      }}
                    />
                    
                    {/* Start year slider */}
                    <input
                      type="range"
                      min={availableYears.min}
                      max={availableYears.max}
                      value={filters.yearRange[0]}
                      onChange={(e) => {
                        const newStart = parseInt(e.target.value);
                        if (newStart <= filters.yearRange[1]) {
                          handleFilterChange('yearRange', [newStart, filters.yearRange[1]]);
                        }
                      }}
                      className="range-slider absolute w-full h-6 bg-transparent cursor-pointer appearance-none"
                      style={{ zIndex: 3 }}
                    />
                    
                    {/* End year slider */}
                    <input
                      type="range"
                      min={availableYears.min}
                      max={availableYears.max}
                      value={filters.yearRange[1]}
                      onChange={(e) => {
                        const newEnd = parseInt(e.target.value);
                        if (newEnd >= filters.yearRange[0]) {
                          handleFilterChange('yearRange', [filters.yearRange[0], newEnd]);
                        }
                      }}
                      className="range-slider absolute w-full h-6 bg-transparent cursor-pointer appearance-none"
                      style={{ zIndex: 2 }}
                    />
                  </div>
                </div>
                
                <div className="flex justify-between text-xs font-medium text-gray-700 px-1">
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    {filters.yearRange[0]}
                  </span>
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    {filters.yearRange[1]}
                  </span>
                </div>
              </div>
            </div>

            {/* Sort By Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <TrendingUp className="inline-block w-4 h-4 mr-2" />
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        <ErrorMessage error={error} />
      </div>
    );
  }

  if (standingsData.length === 0) {
    return (
      <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
        {/* Banner Header */}
        <AllTimeStandingsBanner />

        {/* Filters Card */}
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Game Type Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Filter className="inline-block w-4 h-4 mr-2" />
                Game Types
              </label>
              <div className="space-y-2">
                {gameTypeOptions.map(type => (
                  <label key={type} className="flex items-center cursor-pointer">
                    <input
                      type="checkbox"
                      checked={filters.gameTypes.includes(type)}
                      onChange={(e) => {
                        const newTypes = e.target.checked
                          ? [...filters.gameTypes, type]
                          : filters.gameTypes.filter(t => t !== type);
                        handleFilterChange('gameTypes', newTypes);
                      }}
                      className="mr-3 h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                    />
                    <span className="text-sm text-gray-700">{type}</span>
                  </label>
                ))}
              </div>
            </div>

            {/* Year Range Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <Calendar className="inline-block w-4 h-4 mr-2" />
                Year Range
              </label>
              <div className="space-y-4">
                {/* Custom Range Slider */}
                <div className="relative">
                  <div 
                    className="range-slider-container relative h-2 bg-gray-200 rounded-lg"
                  >
                    <div 
                      className="range-slider-track absolute h-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg"
                      style={{
                        left: `${((filters.yearRange[0] - availableYears.min) / (availableYears.max - availableYears.min)) * 100}%`,
                        width: `${((filters.yearRange[1] - filters.yearRange[0]) / (availableYears.max - availableYears.min)) * 100}%`
                      }}
                    />
                    
                    {/* Start year slider */}
                    <input
                      type="range"
                      min={availableYears.min}
                      max={availableYears.max}
                      value={filters.yearRange[0]}
                      onChange={(e) => {
                        const newStart = parseInt(e.target.value);
                        if (newStart <= filters.yearRange[1]) {
                          handleFilterChange('yearRange', [newStart, filters.yearRange[1]]);
                        }
                      }}
                      className="range-slider absolute w-full h-6 bg-transparent cursor-pointer appearance-none"
                      style={{ zIndex: 3 }}
                    />
                    
                    {/* End year slider */}
                    <input
                      type="range"
                      min={availableYears.min}
                      max={availableYears.max}
                      value={filters.yearRange[1]}
                      onChange={(e) => {
                        const newEnd = parseInt(e.target.value);
                        if (newEnd >= filters.yearRange[0]) {
                          handleFilterChange('yearRange', [filters.yearRange[0], newEnd]);
                        }
                      }}
                      className="range-slider absolute w-full h-6 bg-transparent cursor-pointer appearance-none"
                      style={{ zIndex: 2 }}
                    />
                  </div>
                </div>
                
                <div className="flex justify-between text-xs font-medium text-gray-700 px-1">
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    {filters.yearRange[0]}
                  </span>
                  <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                    {filters.yearRange[1]}
                  </span>
                </div>
              </div>
            </div>

            {/* Sort By Filter */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-3">
                <TrendingUp className="inline-block w-4 h-4 mr-2" />
                Sort By
              </label>
              <select
                value={filters.sortBy}
                onChange={(e) => handleFilterChange('sortBy', e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
              >
                {sortOptions.map(option => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        </div>

        {/* No Data Message */}
        <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-6">
          <h3 className="text-yellow-800 font-semibold mb-2">No data found</h3>
          <p className="text-yellow-700">
            No standings data could be calculated with the current filters. 
            Try adjusting your game type and year range selections above.
          </p>
          <div className="mt-4 text-sm text-yellow-700">
            <p>Current filters:</p>
            <ul className="list-disc list-inside mt-2 space-y-1">
              <li>Game Types: {filters.gameTypes.length > 0 ? filters.gameTypes.join(', ') : 'None selected'}</li>
              <li>Years: {filters.yearRange[0]} - {filters.yearRange[1]}</li>
              <li>Raw H2H records: {rawData.h2hData.length.toLocaleString('en-US')}</li>
            </ul>
          </div>
          {filters.gameTypes.length === 0 && (
            <div className="mt-4 p-3 bg-yellow-100 rounded-lg">
              <p className="text-yellow-800 font-medium">💡 Tip: Select at least one game type to see standings data.</p>
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6 space-y-6">
      {/* Banner Header */}
      <AllTimeStandingsBanner />

      {/* Filters Card */}
      <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 hover:shadow-xl transition-all duration-300">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          {/* Game Type Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <Filter className="inline-block w-4 h-4 mr-2" />
              Game Types
            </label>
            <div className="space-y-2">
              {gameTypeOptions.map(type => (
                <label key={type} className="flex items-center cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.gameTypes.includes(type)}
                    onChange={(e) => {
                      const newTypes = e.target.checked
                        ? [...filters.gameTypes, type]
                        : filters.gameTypes.filter(t => t !== type);
                      handleFilterChange('gameTypes', newTypes);
                    }}
                    className="mr-3 h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                  />
                  <span className="text-sm text-gray-700">{type}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Year Range Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <Calendar className="inline-block w-4 h-4 mr-2" />
              Year Range
            </label>
            <div className="space-y-4">
              {/* Custom Range Slider */}
              <div className="relative">
                <div 
                  className="range-slider-container relative h-2 bg-gray-200 rounded-lg"
                >
                  <div 
                    className="range-slider-track absolute h-2 bg-gradient-to-r from-purple-500 to-indigo-600 rounded-lg"
                    style={{
                      left: `${((filters.yearRange[0] - availableYears.min) / (availableYears.max - availableYears.min)) * 100}%`,
                      width: `${((filters.yearRange[1] - filters.yearRange[0]) / (availableYears.max - availableYears.min)) * 100}%`
                    }}
                  />
                  
                  {/* Start year slider */}
                  <input
                    type="range"
                    min={availableYears.min}
                    max={availableYears.max}
                    value={filters.yearRange[0]}
                    onChange={(e) => {
                      const newStart = parseInt(e.target.value);
                      if (newStart <= filters.yearRange[1]) {
                        handleFilterChange('yearRange', [newStart, filters.yearRange[1]]);
                      }
                    }}
                    className="range-slider absolute w-full h-6 bg-transparent cursor-pointer appearance-none"
                    style={{ zIndex: 2 }}
                  />
                  
                  {/* End year slider */}
                  <input
                    type="range"
                    min={availableYears.min}
                    max={availableYears.max}
                    value={filters.yearRange[1]}
                    onChange={(e) => {
                      const newEnd = parseInt(e.target.value);
                      if (newEnd >= filters.yearRange[0]) {
                        handleFilterChange('yearRange', [filters.yearRange[0], newEnd]);
                      }
                    }}
                    className="range-slider absolute w-full h-6 bg-transparent cursor-pointer appearance-none"
                    style={{ zIndex: 3 }}
                  />
                </div>
              </div>
              
              <div className="flex justify-between text-xs font-medium text-gray-700 px-1">
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  {filters.yearRange[0]}
                </span>
                <span className="bg-purple-100 text-purple-700 px-2 py-1 rounded">
                  {filters.yearRange[1]}
                </span>
              </div>
            </div>
          </div>

          {/* Sort By Filter */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-3">
              <TrendingUp className="inline-block w-4 h-4 mr-2" />
              Sort By
            </label>
            <select
              value={filters.sortBy}
              onChange={(e) => handleFilterChange('sortBy', e.target.value)}
              className="w-full p-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500 transition-all duration-200"
            >
              {sortOptions.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </div>

      {/* Summary Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs sm:text-sm font-medium">Total Managers</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">{standingsData.length}</p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-gradient-to-br from-purple-500 to-indigo-600 text-white">
              <Users className="h-4 w-4 sm:h-6 sm:w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs sm:text-sm font-medium">Total Games</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                {(() => {
                  // Calculate unique games using GameID to avoid double counting
                  const { h2hData } = rawData;
                  if (!h2hData || h2hData.length === 0) return '0';
                  
                  // Filter the same way as standings calculation
                  const filteredH2H = h2hData.filter(game => 
                    filters.gameTypes.includes(game.Type) &&
                    game.Year >= filters.yearRange[0] && 
                    game.Year <= filters.yearRange[1] &&
                    game.Manager1 && 
                    (game.PF !== undefined && game.PF !== null) &&
                    (game.PA !== undefined && game.PA !== null) &&
                    (game.IsWin !== undefined && game.IsWin !== null) &&
                    (game.IsLoss !== undefined && game.IsLoss !== null)
                  );
                  
                  // Use GameID to get unique games (highest GameID value gives total count)
                  if (filteredH2H.length === 0) return '0';
                  
                  const gameIds = filteredH2H.map(game => parseInt(game.GameID)).filter(id => !isNaN(id));
                  if (gameIds.length === 0) {
                    // Fallback: if no GameID, count unique games by dividing total records by 2
                    return Math.floor(filteredH2H.length / 2).toLocaleString('en-US');
                  }
                  
                  const uniqueGameIds = [...new Set(gameIds)];
                  return uniqueGameIds.length.toLocaleString('en-US');
                })()}
              </p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-gradient-to-br from-green-500 to-emerald-600 text-white">
              <Target className="h-4 w-4 sm:h-6 sm:w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs sm:text-sm font-medium">Highest Win %</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                {standingsData.length > 0 ? formatValue(Math.max(...standingsData.map(m => m.WinPct)), 'percentage') : '0%'}
              </p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-gradient-to-br from-yellow-500 to-orange-600 text-white">
              <Trophy className="h-4 w-4 sm:h-6 sm:w-6" />
            </div>
          </div>
        </div>
        
        <div className="bg-white rounded-xl shadow-lg p-4 sm:p-6 border border-gray-100 hover:shadow-xl transition-all duration-300 transform hover:-translate-y-1">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-gray-600 text-xs sm:text-sm font-medium">Avg Points/Game</p>
              <p className="text-xl sm:text-2xl font-bold text-gray-900 mt-1">
                {standingsData.length > 0 ? 
                  (standingsData.reduce((sum, m) => sum + m.AvgPF, 0) / standingsData.length).toFixed(1) : '0'
                }
              </p>
            </div>
            <div className="p-2 sm:p-3 rounded-lg bg-gradient-to-br from-blue-500 to-cyan-600 text-white">
              <TrendingUp className="h-4 w-4 sm:h-6 sm:w-6" />
            </div>
          </div>
        </div>
      </div>

      {/* Standings Table/Cards */}
      <div className="bg-white rounded-xl shadow-lg overflow-hidden border border-gray-100">
        <div className="p-4 sm:p-6 border-b border-gray-200 bg-gray-50">
          <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Current Standings</h2>
          <p className="text-gray-600 mt-1 text-sm sm:text-base">
            Showing {filteredData.length} managers | {filters.gameTypes.join(', ')} | {filters.yearRange[0]}-{filters.yearRange[1]}
          </p>
        </div>
        
        {/* Mobile Cards View */}
        {isMobile ? (
          <div className="p-4 space-y-4">
            {filteredData.map((manager, index) => (
              <MobileManagerCard key={manager.Manager} manager={manager} index={index} />
            ))}
          </div>
        ) : (
          /* Desktop Table View */
          <div className="overflow-x-auto relative">
            <table className="w-full">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Rank</th>
                  <th className="px-6 py-4 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Manager</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">W-L</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Win %</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">GP</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">PF</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">PA</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg PF</th>
                  <th className="px-6 py-4 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">Avg PA</th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {filteredData.map((manager, index) => (
                  <tr 
                    key={manager.Manager} 
                    className="hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-full text-sm font-bold ${
                          index === 0 ? 'bg-yellow-400 text-yellow-900' :
                          index === 1 ? 'bg-gray-400 text-gray-900' :
                          index === 2 ? 'bg-amber-400 text-amber-900' :
                          'bg-gray-100 text-gray-700'
                        }`}>
                          {index + 1}
                        </span>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center space-x-4">
                        {/* Manager Avatar */}
                        <div className="w-12 h-12 rounded-xl overflow-hidden border-2 border-gray-200 shadow-sm bg-gradient-to-br from-purple-100 to-blue-100 flex-shrink-0">
                          {!imageErrors.has(manager.Manager) ? (
                            <img 
                              src={`/${manager.Manager.toLowerCase()}main.png`} 
                              alt={`${manager.Manager} headshot`}
                              className="w-full h-full object-cover"
                              onError={() => handleImageError(manager.Manager)}
                            />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-lg font-bold text-purple-600">
                              {manager.Manager.charAt(0)}
                            </div>
                          )}
                        </div>
                        
                        {/* Manager Name */}
                        <div className="font-medium text-gray-900 text-lg">
                          {manager.Manager}
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900 font-medium">
                      {manager.Wins}-{manager.Losses}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-semibold">
                      <span className={manager.WinPct >= 0.6 ? 'text-green-600' : manager.WinPct >= 0.4 ? 'text-yellow-600' : 'text-red-600'}>
                        {formatValue(manager.WinPct, 'percentage')}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatValue(manager.GP, 'number')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatValue(manager.PF, 'decimal_with_commas')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatValue(manager.PA, 'decimal_with_commas')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatValue(manager.AvgPF, 'decimal')}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-right text-sm text-gray-900">
                      {formatValue(manager.AvgPA, 'decimal')}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Footer Notes */}
      <div className="bg-purple-50 border border-purple-200 rounded-xl p-4 sm:p-6">
        <h3 className="text-lg font-semibold text-purple-900 mb-2">About All-Time Standings</h3>
        <ul className="text-purple-800 space-y-1 text-sm">
          <li>• Rankings are based on win percentage, with total points for as the primary tiebreaker</li>
          <li>• Data includes all head-to-head matchups based on your selected filters</li>
          <li>• Use the filters above to customize the view by game type and year range</li>
          <li>• PF = Points For, PA = Points Against, GP = Games Played</li>
          <li>• Year range is dynamically set based on available data ({availableYears.min}-{availableYears.max})</li>
        </ul>
      </div>
    </div>
  );
};

// Add styles for the range slider
const sliderStyles = `
  .range-slider {
    -webkit-appearance: none;
    appearance: none;
    background: transparent;
    cursor: pointer;
    pointer-events: none;
  }
  
  .range-slider::-webkit-slider-track {
    background: transparent;
    height: 20px;
  }
  
  .range-slider::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #8b5cf6;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    border: 3px solid white;
    transition: all 0.2s ease;
    pointer-events: auto;
  }
  
  .range-slider::-webkit-slider-thumb:hover {
    background: #7c3aed;
    transform: scale(1.1);
  }
  
  .range-slider::-moz-range-track {
    background: transparent;
    height: 20px;
    border: none;
  }
  
  .range-slider::-moz-range-thumb {
    height: 20px;
    width: 20px;
    border-radius: 50%;
    background: #8b5cf6;
    cursor: pointer;
    border: 3px solid white;
    box-shadow: 0 2px 6px rgba(0,0,0,0.2);
    transition: all 0.2s ease;
    pointer-events: auto;
  }
  
  .range-slider::-moz-range-thumb:hover {
    background: #7c3aed;
    transform: scale(1.1);
  }
  
  .range-slider:focus::-webkit-slider-thumb {
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
  }
  
  .range-slider:focus::-moz-range-thumb {
    box-shadow: 0 0 0 3px rgba(139, 92, 246, 0.3);
  }

  .range-slider-min {
    z-index: 2;
  }
  
  .range-slider-max {
    z-index: 1;
  }
`;

// Inject styles
if (typeof document !== 'undefined') {
  const styleElement = document.createElement('style');
  styleElement.textContent = sliderStyles;
  document.head.appendChild(styleElement);
}

export default AllTimeStandingsComponent;